import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { hrmApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
  StatusBadge,
} from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June',
                 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * Payroll — FR-HR-04
 *
 * Monthly payroll runs with lifecycle: draft → calculated → approved → disbursed.
 * Two-pane list + detail. Lifecycle actions on the right per run status.
 */
export function PayrollPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');

  const listQ = useQuery({
    queryKey: ['payroll-runs'],
    queryFn: () => hrmApi.listPayrollRuns({ limit: 100 }),
  });

  const { kpis, filteredRows } = useMemo(() => {
    const rows = (listQ.data ?? []) as any[];
    const k = {
      total: rows.length,
      pending: rows.filter((r) => ['draft', 'calculated'].includes(r.status)).length,
      awaitingApproval: rows.filter((r) => r.status === 'calculated').length,
      disbursed: rows.filter((r) => ['disbursed', 'locked'].includes(r.status)).length,
    };
    const filtered = statusFilter ? rows.filter((r) => r.status === statusFilter) : rows;
    return { kpis: k, filteredRows: filtered };
  }, [listQ.data, statusFilter]);

  const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
    { value: '',           label: 'All' },
    { value: 'draft',      label: 'Draft' },
    { value: 'calculated', label: 'Calculated' },
    { value: 'approved',   label: 'Approved' },
    { value: 'disbursed',  label: 'Disbursed' },
    { value: 'locked',     label: 'Locked' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="People"
        title="Payroll"
        subtitle="Monthly payroll runs: draft → calculated → approved → disbursed to GL."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            <PlusIcon /> New payroll run
          </button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total runs" value={kpis.total} />
        <KpiTile label="In progress" value={kpis.pending}
          accent={kpis.pending > 0 ? 'amber' : 'default'} />
        <KpiTile label="Awaiting approval" value={kpis.awaitingApproval}
          accent={kpis.awaitingApproval > 0 ? 'amber' : 'default'} />
        <KpiTile label="Disbursed" value={kpis.disbursed} accent="sage" />
      </div>

      {/* Status filter chips */}
      <div className="card p-1.5 mb-4 flex gap-1 items-center overflow-x-auto">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            className={clsx(
              'px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap',
              statusFilter === s.value
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Two-pane */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_34rem] gap-6">
        <div className="card overflow-hidden">
          {listQ.isLoading ? (
            <LoadingState rows={5} />
          ) : listQ.error ? (
            <ErrorState error={listQ.error} />
          ) : !filteredRows.length ? (
            <EmptyState
              message={statusFilter ? `No ${statusFilter} runs` : 'No payroll runs'}
              hint={statusFilter
                ? 'Try a different filter.'
                : 'Create a monthly run to begin.'}
              action={!statusFilter && (
                <button className="btn-primary" onClick={() => setShowCreate(true)}>
                  <PlusIcon /> New payroll run
                </button>
              )}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Run</th>
                    <th>Period</th>
                    <th>Status</th>
                    <th className="text-right">Lines</th>
                    <th className="text-right">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r: any) => (
                    <tr
                      key={r.id}
                      onClick={() => setSelected(r.id)}
                      className={clsx('cursor-pointer', selected === r.id && '!bg-canvas-subtle')}
                    >
                      <td className="font-mono text-xs font-semibold text-ink-900">{r.run_number}</td>
                      <td className="text-sm text-ink-700 font-medium">
                        {MONTHS[r.period_month]} {r.period_year}
                      </td>
                      <td><PayrollStatusBadge status={r.status} /></td>
                      <td className="text-right text-sm font-mono text-ink-500 tabular-nums">
                        {r.line_count}
                      </td>
                      <td className="text-right font-mono font-semibold text-ink-900 tabular-nums">
                        ${parseFloat(r.total_net).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card p-5 min-h-[400px]">
          {!selected ? (
            <EmptyState
              message="Select a payroll run"
              hint="Click a row on the left to view lines and lifecycle actions."
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                  <line x1="9" y1="3" x2="9" y2="21"/>
                </svg>
              }
            />
          ) : (
            <RunDetail runId={selected}
              onChanged={() => qc.invalidateQueries({ queryKey: ['payroll-runs'] })} />
          )}
        </div>
      </div>

      {showCreate && (
        <CreateRunModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            qc.invalidateQueries({ queryKey: ['payroll-runs'] });
            setShowCreate(false);
            setSelected(id);
          }}
        />
      )}
    </div>
  );
}

function PayrollStatusBadge({ status }: { status: string }) {
  const tone =
    status === 'draft'      ? 'neutral' :
    status === 'calculated' ? 'info' :
    status === 'approved'   ? 'sage' :
    status === 'disbursed'  ? 'sage' :
    status === 'locked'     ? 'sage' :
                              'neutral';
  return <StatusBadge status={status} tone={tone as any} />;
}

// ============================================================
// RunDetail — the workflow panel
// ============================================================
function RunDetail({ runId, onChanged }: { runId: string; onChanged: () => void }) {
  const qc = useQueryClient();
  const [payslipFor, setPayslipFor] = useState<string | null>(null);
  const [showDisburse, setShowDisburse] = useState(false);

  const q = useQuery({
    queryKey: ['payroll-run', runId],
    queryFn: () => hrmApi.getPayrollRun(runId),
  });

  const calcMut = useMutation({
    mutationFn: () => hrmApi.calculatePayrollRun(runId),
    onSuccess: (d) => {
      toast.success(`Calculated · ${d.employeeCount} employees · Net ${fmtCurrency(d.totalNet)}`, { duration: 6000 });
      qc.invalidateQueries({ queryKey: ['payroll-run', runId] });
      onChanged();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const approveMut = useMutation({
    mutationFn: () => hrmApi.approvePayrollRun(runId),
    onSuccess: () => { toast.success('Approved'); qc.invalidateQueries({ queryKey: ['payroll-run', runId] }); onChanged(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const disburseMut = useMutation({
    mutationFn: () => hrmApi.disbursePayrollRun(runId),
    onSuccess: (d) => {
      toast.success(`Disbursed · JE ${d.journalEntryNumber} posted · Net ${fmtCurrency(d.totalNet)}`, { duration: 6000 });
      qc.invalidateQueries({ queryKey: ['payroll-run', runId] });
      onChanged();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return <LoadingState rows={4} />;
  if (q.error) return <ErrorState error={q.error} />;
  const run = q.data;

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="min-w-0">
          <div className="font-mono text-xs text-ink-500 mb-0.5">{run.run_number}</div>
          <div className="font-display font-bold text-lg text-ink-900">
            {MONTHS[run.period_month]} {run.period_year}
          </div>
          <div className="text-xs text-ink-500 mt-1">
            {new Date(run.period_from).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            {' → '}
            {new Date(run.period_to).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
          {run.payment_date && (
            <div className="text-xs text-ink-500 mt-0.5">
              Pay date: {new Date(run.payment_date).toLocaleDateString()}
            </div>
          )}
        </div>
        <PayrollStatusBadge status={run.status} />
      </div>

      {run.notes && (
        <div className="bg-canvas-subtle rounded-xl p-3 mb-4 text-xs text-ink-600">
          {run.notes}
        </div>
      )}

      {/* Totals card — the hero */}
      <div className="bg-canvas-subtle rounded-xl p-4 mb-4">
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <div className="eyebrow text-ink-500">Gross</div>
            <div className="font-mono font-semibold text-ink-700 tabular-nums mt-1">
              ${parseFloat(run.total_gross).toFixed(2)}
            </div>
          </div>
          <div>
            <div className="eyebrow text-ink-500">Deductions</div>
            <div className="font-mono font-semibold text-danger tabular-nums mt-1">
              −${parseFloat(run.total_deductions).toFixed(2)}
            </div>
          </div>
          <div>
            <div className="eyebrow text-sage-700">Net</div>
            <div className="font-display font-bold text-2xl text-ink-900 tabular-nums tracking-tight mt-0.5">
              ${parseFloat(run.total_net).toFixed(2)}
            </div>
          </div>
        </div>
      </div>

      {/* JE reference */}
      {run.journal_entry_number && (
        <div className="bg-sage-50 border border-sage-200 rounded-xl px-3 py-2 text-xs text-sage-800 mb-4">
          Journal entry posted: <span className="font-mono font-semibold">{run.journal_entry_number}</span>
        </div>
      )}

      {/* Timeline */}
      {(run.calculated_at || run.approved_at || run.disbursed_at) && (
        <div className="bg-canvas-subtle rounded-xl p-3 mb-4 space-y-1.5 text-xs text-ink-600">
          {run.calculated_at && (
            <div>
              <span className="text-ink-400">Calculated:</span>{' '}
              {new Date(run.calculated_at).toLocaleString()} · {run.calculated_by_name}
            </div>
          )}
          {run.approved_at && (
            <div>
              <span className="text-ink-400">Approved:</span>{' '}
              {new Date(run.approved_at).toLocaleString()} · {run.approved_by_name}
            </div>
          )}
          {run.disbursed_at && (
            <div className="text-sage-700">
              <span className="text-sage-600">Disbursed:</span>{' '}
              {new Date(run.disbursed_at).toLocaleString()} · {run.disbursed_by_name}
            </div>
          )}
        </div>
      )}

      {/* Lines */}
      {run.lines.length > 0 && (
        <div className="mb-4">
          <div className="eyebrow text-ink-500 mb-2">Lines ({run.lines.length})</div>
          <div className="border border-ink-100 rounded-xl overflow-hidden max-h-80 overflow-y-auto">
            <table className="table text-xs">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th className="text-right">Gross</th>
                  <th className="text-right">Deductions</th>
                  <th className="text-right">Net</th>
                  <th className="w-14"></th>
                </tr>
              </thead>
              <tbody>
                {run.lines.map((l: any) => (
                  <tr key={l.id}>
                    <td>
                      <div className="font-medium text-ink-900">{l.full_name}</div>
                      <div className="font-mono text-2xs text-ink-500">{l.employee_code}</div>
                    </td>
                    <td className="text-right font-mono tabular-nums">{fmtCurrency(parseFloat(l.gross_pay))}</td>
                    <td className="text-right font-mono text-danger tabular-nums">−${parseFloat(l.total_deductions).toFixed(2)}</td>
                    <td className="text-right font-mono font-semibold tabular-nums">{fmtCurrency(parseFloat(l.net_pay))}</td>
                    <td className="text-right">
                      <button
                        className="text-xs font-medium text-ink-600 hover:text-ink-900 transition-colors"
                        onClick={() => setPayslipFor(l.employee_id)}
                      >
                        Payslip →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Workflow actions */}
      <div className="flex gap-2 justify-end flex-wrap">
        {['draft', 'calculated'].includes(run.status) && (
          <button
            className="btn-primary"
            disabled={calcMut.isPending}
            onClick={() => calcMut.mutate()}
          >
            {calcMut.isPending ? 'Calculating…' : run.status === 'draft' ? 'Calculate' : 'Recalculate'}
          </button>
        )}
        {run.status === 'calculated' && (
          <button
            className="btn-primary"
            disabled={approveMut.isPending}
            onClick={() => approveMut.mutate()}
          >
            {approveMut.isPending ? 'Approving…' : 'Approve'}
          </button>
        )}
        {run.status === 'approved' && (
          <button
            className="btn-primary"
            disabled={disburseMut.isPending}
            onClick={() => setShowDisburse(true)}
          >
            {disburseMut.isPending ? 'Disbursing…' : 'Disburse + Post JE'}
          </button>
        )}
      </div>

      {payslipFor && (
        <PayslipModal
          runId={runId}
          employeeId={payslipFor}
          onClose={() => setPayslipFor(null)}
        />
      )}

      {showDisburse && (
        <Modal
          onClose={() => setShowDisburse(false)}
          overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <div className="px-6 pt-6 pb-2">
            <div className="eyebrow text-danger mb-1">HRM · Payroll</div>
            <h2 className="text-xl font-bold font-display text-ink-900">Disburse payroll?</h2>
            <p className="text-sm text-ink-500 mt-2 leading-relaxed">
              This will post a journal entry to the GL for{' '}
              <span className="font-semibold text-ink-900">{run.run_number}</span> and mark the run as disbursed.
              <span className="text-danger font-semibold"> This cannot be reversed.</span>
            </p>
          </div>
          <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
            <button className="btn-secondary" onClick={() => setShowDisburse(false)}>Cancel</button>
            <button
              className="btn-primary !bg-danger hover:!bg-red-700"
              disabled={disburseMut.isPending}
              onClick={() => { disburseMut.mutate(); setShowDisburse(false); }}
            >
              {disburseMut.isPending ? 'Disbursing…' : 'Disburse + Post JE'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============================================================
// PayslipModal
// ============================================================
function PayslipModal({ runId, employeeId, onClose }:
    { runId: string; employeeId: string; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['payslip', runId, employeeId],
    queryFn: () => hrmApi.getPayslip(runId, employeeId),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
      <div className="px-6 pt-6 pb-4 flex justify-between items-start flex-shrink-0">
        <div>
          <div className="eyebrow mb-1">People · Payroll</div>
          <h2 className="text-xl font-bold font-display text-ink-900">Payslip</h2>
        </div>
        <button
          className="w-8 h-8 flex items-center justify-center text-ink-400 hover:text-ink-900 hover:bg-canvas-subtle rounded-md transition-colors"
          onClick={onClose}
          aria-label="Close"
        >✕</button>
      </div>

      <div className="px-6 flex-1 overflow-y-auto">
        {q.isLoading ? <LoadingState rows={4} /> : q.error ? <ErrorState error={q.error} /> : (() => {
          const p = q.data;
          return (
            <div>
              <div className="grid grid-cols-2 gap-3 pb-4 border-b border-ink-100 mb-4">
                <div>
                  <div className="eyebrow text-ink-500">Employee</div>
                  <div className="font-semibold text-ink-900 mt-1">{p.employee.fullName}</div>
                  <div className="font-mono text-xs text-ink-500">
                    {p.employee.employeeCode}{p.employee.designation && ` · ${p.employee.designation}`}
                  </div>
                  {p.employee.department && (
                    <div className="text-xs text-ink-500">{p.employee.department}</div>
                  )}
                </div>
                <div className="text-right">
                  <div className="eyebrow text-ink-500">Pay period</div>
                  <div className="font-semibold text-ink-900 mt-1">{MONTHS[p.periodMonth]} {p.periodYear}</div>
                  <div className="text-xs text-ink-500">
                    {new Date(p.periodFrom).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    {' → '}
                    {new Date(p.periodTo).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </div>
                  <div className="mt-2 inline-block">
                    <PayrollStatusBadge status={p.status} />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-canvas-subtle rounded-xl p-4">
                  <div className="eyebrow text-sage-700 mb-3">Earnings</div>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-ink-600">Base salary</span>
                      <span className="font-mono tabular-nums">{fmtCurrency(p.earnings.baseSalary)}</span>
                    </div>
                    {p.earnings.allowances > 0 && (
                      <div className="flex justify-between">
                        <span className="text-ink-600">Allowances</span>
                        <span className="font-mono tabular-nums">{fmtCurrency(p.earnings.allowances)}</span>
                      </div>
                    )}
                    {p.earnings.overtimePay > 0 && (
                      <div className="flex justify-between">
                        <span className="text-ink-600">Overtime pay</span>
                        <span className="font-mono tabular-nums">{fmtCurrency(p.earnings.overtimePay)}</span>
                      </div>
                    )}
                    {p.earnings.bonus > 0 && (
                      <div className="flex justify-between">
                        <span className="text-ink-600">Bonus</span>
                        <span className="font-mono tabular-nums">{fmtCurrency(p.earnings.bonus)}</span>
                      </div>
                    )}
                    {p.deductions.leaveDeduction > 0 && (
                      <div className="flex justify-between text-danger">
                        <span>Unpaid leave</span>
                        <span className="font-mono tabular-nums">−${p.deductions.leaveDeduction.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-between items-baseline pt-3 mt-3 border-t border-ink-200">
                    <span className="font-semibold text-ink-900">Gross pay</span>
                    <span className="font-mono font-bold text-ink-900 tabular-nums">
                      ${p.earnings.grossPay.toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className="bg-canvas-subtle rounded-xl p-4">
                  <div className="eyebrow text-danger mb-3">Deductions</div>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-ink-600">Income tax</span>
                      <span className="font-mono tabular-nums">{fmtCurrency(p.deductions.tax)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-600">Social security</span>
                      <span className="font-mono tabular-nums">{fmtCurrency(p.deductions.socialSecurity)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-600">Insurance</span>
                      <span className="font-mono tabular-nums">{fmtCurrency(p.deductions.insurance)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-600">Pension</span>
                      <span className="font-mono tabular-nums">{fmtCurrency(p.deductions.pension)}</span>
                    </div>
                    {p.deductions.other > 0 && (
                      <div className="flex justify-between">
                        <span className="text-ink-600">Other</span>
                        <span className="font-mono tabular-nums">{fmtCurrency(p.deductions.other)}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-between items-baseline pt-3 mt-3 border-t border-ink-200">
                    <span className="font-semibold text-ink-900">Total deductions</span>
                    <span className="font-mono font-bold text-ink-900 tabular-nums">
                      ${p.deductions.total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Net pay hero */}
              <div className="bg-gradient-to-b from-sage-50 to-canvas-elevated rounded-xl p-6 text-center border border-sage-100 mb-4">
                <div className="eyebrow text-sage-700 mb-1">Net pay</div>
                <div className="font-display font-bold text-4xl text-ink-900 tabular-nums tracking-tight">
                  ${p.netPay.toFixed(2)}
                </div>
              </div>

              {/* Attendance footer */}
              <div className="grid grid-cols-3 gap-2 mb-6">
                <div className="bg-canvas-subtle rounded-md p-3 text-center">
                  <div className="eyebrow text-2xs text-ink-500">Days present</div>
                  <div className="font-mono font-bold text-ink-900 tabular-nums mt-1">
                    {p.attendance.daysPresent}
                  </div>
                </div>
                <div className="bg-canvas-subtle rounded-md p-3 text-center">
                  <div className="eyebrow text-2xs text-ink-500">OT hours</div>
                  <div className="font-mono font-bold text-ink-900 tabular-nums mt-1">
                    {p.attendance.overtimeHours}
                  </div>
                </div>
                <div className="bg-canvas-subtle rounded-md p-3 text-center">
                  <div className="eyebrow text-2xs text-ink-500">Unpaid leave</div>
                  <div className="font-mono font-bold text-ink-900 tabular-nums mt-1">
                    {p.attendance.unpaidLeaveDays}d
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Close</button>
        <button className="btn-primary" onClick={() => window.print()}>
          Print
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// CreateRunModal
// ============================================================
function CreateRunModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const now = new Date();
  const [periodYear, setPeriodYear] = useState(now.getFullYear());
  const [periodMonth, setPeriodMonth] = useState(now.getMonth() + 1);
  const [paymentDate, setPaymentDate] = useState('');
  const [notes, setNotes] = useState('');

  const mut = useMutation({
    mutationFn: () => hrmApi.createPayrollRun({
      periodYear, periodMonth,
      paymentDate: paymentDate || undefined,
      notes: notes || undefined,
    }),
    onSuccess: (d) => { toast.success(`Created ${d.runNumber}`); onCreated(d.id); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">People · Payroll</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New payroll run</h2>
        <p className="text-sm text-ink-500 mt-1">
          Creates a draft run for the period. You'll calculate, review, approve, and disburse in sequence.
        </p>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Month</label>
            <select
              className="input"
              value={periodMonth}
              onChange={(e) => setPeriodMonth(parseInt(e.target.value))}
            >
              {MONTHS.slice(1).map((m, i) => (
                <option key={i + 1} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Year</label>
            <input
              type="number" min={2020} max={2100}
              className="input tabular-nums"
              value={periodYear}
              onChange={(e) => setPeriodYear(parseInt(e.target.value) || now.getFullYear())}
            />
          </div>
        </div>
        <div>
          <label className="label">Payment date <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input type="date" className="input" value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)} />
        </div>
        <div>
          <label className="label">Notes <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <textarea className="input" rows={2} value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Quarter-end bonus run, mid-month advance, etc." />
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create run'}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// Shared
// ============================================================
const iconProps = {
  width: 16, height: 16, viewBox: '0 0 24 24',
  fill: 'none', stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};
function PlusIcon() { return <svg {...iconProps}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}
